import { Component, CSSProperties, ReactNode } from "react";
import Spinner from "~/components/shared/Spinner";

interface RemoteButtonComponentProps {
  url: string;
  method: string;
  onSuccess?(event: Event, response: any): void;
  onError?(event: Event, response: any): void;
  onBeforeStart?: () => void;
  loading?: boolean;
  loadingText?: string;
  loadingIndicator?: ReactNode;
  children?: ReactNode;
  tooltipText?: string;
  confirmText?: string;
  style?: CSSProperties;
  className?: string;
  title?: string;
}

interface RemoteButtonComponentState {
  loading: boolean;
}

export default class RemoteButtonComponent extends Component<
  RemoteButtonComponentProps,
  RemoteButtonComponentState
> {
  static displayName = "RemoteButtonComponent";

  static defaultProps = {
    loadingIndicator: <Spinner className="mx-2" size={16} color={false} />,
  };

  buttonNode: HTMLAnchorElement | null = null;

  state: RemoteButtonComponentState = { loading: false };

  componentDidMount() {
    this.buttonNode?.addEventListener("ajax:beforeSend", this._onAjaxBeforeSend);
    this.buttonNode?.addEventListener("ajax:success", this._onAjaxSuccess);
    this.buttonNode?.addEventListener("ajax:error", this._onAjaxError);
  }

  componentWillUnmount() {
    this.buttonNode?.removeEventListener("ajax:beforeSend", this._onAjaxBeforeSend);
    this.buttonNode?.removeEventListener("ajax:success", this._onAjaxSuccess);
    this.buttonNode?.removeEventListener("ajax:error", this._onAjaxError);
  }

  _onAjaxBeforeSend = () => {
    if (this.props.onBeforeStart) {
      this.props.onBeforeStart();
    }

    this.setState({ loading: true });
  };

  _onAjaxSuccess = (event: Event) => {
    const customEvent = event as CustomEvent;
    const [response] = customEvent.detail;

    this.setState({ loading: false });

    if (this.props.onSuccess) {
      this.props.onSuccess(event, response);
    }
  };

  _onAjaxError = (event: Event) => {
    const customEvent = event as CustomEvent;
    const [response] = customEvent.detail;

    this.setState({ loading: false });

    if (this.props.onError) {
      this.props.onError(event, response);
    } else {
      if (response.message) {
        alert(response.message);
      }
    }
  };

  render() {
    const contents =
      this.state.loading || this.props.loading ? (
        <>
          {this.props.loadingIndicator} {this.props.loadingText}
        </>
      ) : (
        this.props.children
      );

    const attributes: { "aria-label"?: string; "data-title-tooltip"?: string } = {};

    if (this.props.tooltipText) {
      attributes["aria-label"] = this.props.tooltipText;
      attributes["data-title-tooltip"] = this.props.tooltipText;
    }

    return (
      <a
        ref={(buttonNode) => (this.buttonNode = buttonNode)}
        href={this.props.url}
        data-method={this.props.method}
        data-remote="true"
        data-confirm={this.props.confirmText}
        aria-disabled={this.state.loading || this.props.loading}
        style={this.props.style}
        rel="nofollow"
        className={this.props.className}
        title={this.props.title}
        {...attributes}
      >
        {contents}
      </a>
    );
  }
}
